import { ProductRewardSearchResult } from "@/types/search";
import { styled } from "@mui/material/styles";
import { useInView } from "motion/react";
import _orderBy from "lodash/orderBy";
import { useEffect, useMemo, useRef } from "react";
import { defaultPreviewLazyLoadConfig } from "../../../../../utils/lazyLoadConfig";
import StoreLoadingSkeleton from "../../../../StoreComponents/StoreLoadingSkeleton";
import { DEFAULT_MIN_CARD_WIDTH } from "../components/Constant";
import { PreviewHeader } from "../components/Header";
import PreviewContainer from "../components/PreviewContainer";
import PreviewWidgetWrapper from "../components/PreviewWidgetWrapper";
import { DealCard } from "./components/DealCard";
import { useFetchDailyDeals } from "./data";

export const DealCardGrid = styled("div", {
  label: "DealCardGrid",
})(() => {
  return {
    width: "100%",
    display: "grid",
    gridTemplateColumns: `repeat(auto-fill, minmax(${DEFAULT_MIN_CARD_WIDTH}px, 1fr))`,
    justifyContent: "space-between",
    gridGap: 20,
  };
});

const DailyDealsPreview = () => {
  const ref = useRef(null);
  const widgetInView = useInView(ref, defaultPreviewLazyLoadConfig);
  const { data, loading, fetchInit } = useFetchDailyDeals({
    pageSize: 5,
    autoInitFetch: false,
  });

  useEffect(() => {
    if (widgetInView) {
      fetchInit();
    }
  }, [widgetInView, fetchInit]);

  const sortedDeals = useMemo(() => {
    const MAX_REWARD_PERCENT = 0.25;
    const rewardPercentWeight = 1;

    return _orderBy(
      data?.map((deal) => {
        const { dailyDealRewardInfo, retailPrice } = deal;
        const { rewardAmount } = dailyDealRewardInfo ?? { rewardAmount: 0 };
        const percentReward = retailPrice ? rewardAmount / retailPrice : 0;
        const rewardPercentBoost =
          (rewardPercentWeight * percentReward) / MAX_REWARD_PERCENT;
        return {
          ...deal,
          sortScore: rewardPercentBoost,
        };
      }),
      ["sortScore"],
      ["desc"]
    );
  }, [data]);

  if (!loading && data.length === 0) {
    return null;
  }

  return (
    <PreviewWidgetWrapper ref={ref} backgroundColor="white">
      <PreviewContainer>
        {loading ? (
          <StoreLoadingSkeleton header />
        ) : (
          <>
            <PreviewHeader
              title={"Daily deals"}
              linkTo="/dailydeals"
              subtitle="Access the top deals and rewards"
              indicator="grey"
            />

            <DealCardGrid>
              {sortedDeals.map((d: ProductRewardSearchResult) => (
                <DealCard product={d} key={d.stacklineSku} />
              ))}
            </DealCardGrid>
          </>
        )}
      </PreviewContainer>
    </PreviewWidgetWrapper>
  );
};

export default DailyDealsPreview;
